import { useEffect, useState } from 'react'
import { Check, X, Plus, ArrowRight } from 'lucide-react'
import { useTenant } from '../contexts/TenantContext'
import { useBasket } from '../contexts/BasketContext'
import { useCurrency } from '../contexts/CurrencyContext'
import { getCategories } from '../utils/tebex'

// Global "Added to your cart" confirmation with a small cross-sell. Tebex's
// Headless API doesn't expose sales counts, so these are hand-picked.
const CROSS_SELL = [
  { id: 6145337, label: 'Gold VIP' },
  { id: 6123188, label: '1000 Gems Pack' },
  { id: 6500802, label: 'Socials Verification' },
  { id: 6146824, label: 'Diamond VIP' },
  { id: 6126609, label: '2000 Gems Pack' },
  { id: 6500791, label: 'Discord GIF Perms' },
  { id: 7299024, label: 'Radio Channel Lock' },
]

export default function AddedToCartModal() {
  const tenant = useTenant()
  const token = tenant && tenant.tebex && tenant.tebex.publicToken
  const { enabled, addedItems, addedOpen, closeAdded, addToCart, openCart, items } = useBasket()
  const { format } = useCurrency()
  const [pkgById, setPkgById] = useState(null)
  const [shown, setShown] = useState([])

  useEffect(() => {
    if (!addedOpen || pkgById || !token) return
    let cancelled = false
    getCategories(token)
      .then((cats) => { if (!cancelled) setPkgById(new Map((cats || []).flatMap((c) => c.packages || []).map((p) => [String(p.id), p]))) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [addedOpen, pkgById, token])

  // Freeze suggestions while open so adding one flips it in place.
  useEffect(() => {
    if (!addedOpen || !pkgById) return
    const exclude = new Set([...items.map((p) => p.id), ...addedItems.map((p) => p.id)])
    const hasSub = items.some((p) => p.sub) || addedItems.some((p) => p.type === 'subscription')
    setShown(CROSS_SELL.map((c) => ({ ...c, pkg: pkgById.get(String(c.id)) }))
      .filter((c) => c.pkg && !exclude.has(c.pkg.id) && !(hasSub && c.pkg.type === 'subscription')).slice(0, 3))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addedOpen, pkgById])

  useEffect(() => {
    if (!addedOpen) return
    const onKey = (e) => { if (e.key === 'Escape') closeAdded() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [addedOpen, closeAdded])

  if (!enabled || !addedOpen) return null
  const inBasket = new Set(items.map((p) => p.id))
  const first = addedItems[0]

  return (
    <div className="mw-public !bg-transparent fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-6" role="dialog" aria-modal="true" aria-label="Added to cart">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm mw-fade" onClick={closeAdded} />
      <div className="relative w-full max-w-md bg-[#0e1015] border border-white/[0.08] rounded-t-[28px] sm:rounded-[28px] p-2 mw-rise shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)]" style={{ animationDuration: '0.45s' }}>
        <button onClick={closeAdded} aria-label="Close" className="absolute top-4 right-4 mw-icon-btn !w-9 !h-9 !bg-transparent hover:!bg-white/[0.08] hover:!text-white text-[#8b8d94]"><X /></button>

        <div className="p-3 pr-14 flex gap-4 items-center rounded-[22px] bg-white/[0.04]">
          {first && first.image && <img src={first.image} alt="" className="w-16 h-16 rounded-[14px] object-cover shrink-0" />}
          <div className="min-w-0 pr-8">
            <p className="flex items-center gap-1.5 text-[13px] font-medium text-[#34d399]"><span className="w-5 h-5 rounded-full bg-[#34d399]/15 flex items-center justify-center"><Check className="w-3 h-3" /></span> Added to cart</p>
            <p className="mt-1.5 text-[16px] text-white leading-snug">{addedItems.map((p) => p.name).join(' + ')}</p>
          </div>
        </div>

        {shown.length > 0 && (
          <div className="px-4 pt-5">
            <p className="text-[13px] font-medium text-[#8b8d94] mb-2">Often added with this</p>
            <div>
              {shown.map((c) => {
                const added = inBasket.has(c.pkg.id)
                return (
                  <div key={c.id} className="flex items-center gap-3 py-2">
                    <div className="w-11 h-11 shrink-0 rounded-xl bg-black overflow-hidden">{c.pkg.image && <img src={c.pkg.image} alt="" className="w-full h-full object-cover" />}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-white truncate">{c.label}</p>
                      <p className="text-[12px] text-[#8b8d94] tabular-nums">{format(c.pkg.total_price != null ? c.pkg.total_price : c.pkg.base_price)}{c.pkg.type === 'subscription' ? ' /mo' : ''}</p>
                    </div>
                    {added ? (
                      <span className="mw-icon-btn is-done"><Check /></span>
                    ) : (
                      <button onClick={() => addToCart(c.pkg)} aria-label={'Add ' + c.label} className="mw-icon-btn"><Plus /></button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        <div className="p-4 pt-5 grid grid-cols-2 gap-2">
          <button onClick={closeAdded} className="mw-btn mw-btn-ghost">Keep shopping</button>
          <button onClick={() => { closeAdded(); openCart() }} className="mw-btn mw-btn-primary">View cart <ArrowRight className="mw-arrow" /></button>
        </div>
      </div>
    </div>
  )
}
